import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Phone,
  Email,
  LocationOn,
  AccessTime,
  Facebook,
  YouTube,
  Instagram,
  Send,
  Call,
  OpenInNew,
  Storefront,
  Business,
  Stars,
  Twitter,
  LinkedIn,
} from '@mui/icons-material';
import { useConfig } from '../utils/useConfig';
import Seo from '../components/seo/Seo';

// Definicje typów
type ColorKey = 'blue' | 'green' | 'red' | 'amber' | 'purple' | 'indigo';

type ColorVariant = {
  light: string;
  medium: string;
  accent: string;
  hover: string;
  icon: string;
  border: string;
  gradient: string;
};

type ContactCard = {
  id: string;
  icon: JSX.Element;
  title: string;
  content: JSX.Element;
  action?: {
    text: string;
    url: string;
    icon?: JSX.Element;
  };
  color: ColorKey;
};

function Contact() {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const config = useConfig(); // Pobieramy konfigurację

  // Definicja wariantów kolorystycznych
  const colorVariants: Record<ColorKey, ColorVariant> = {
    blue: {
      light: 'bg-blue-50',
      medium: 'bg-blue-100',
      accent: 'text-blue-600',
      hover: 'group-hover:bg-blue-600',
      icon: 'bg-blue-100 text-blue-600',
      border: 'border-blue-200',
      gradient: 'from-blue-50 to-white',
    },
    green: {
      light: 'bg-green-50',
      medium: 'bg-green-100',
      accent: 'text-green-600',
      hover: 'group-hover:bg-green-600',
      icon: 'bg-green-100 text-green-600',
      border: 'border-green-200',
      gradient: 'from-green-50 to-white',
    },
    red: {
      light: 'bg-red-50',
      medium: 'bg-red-100',
      accent: 'text-red-600',
      hover: 'group-hover:bg-red-600',
      icon: 'bg-red-100 text-red-600',
      border: 'border-red-200',
      gradient: 'from-red-50 to-white',
    },
    amber: {
      light: 'bg-amber-50',
      medium: 'bg-amber-100',
      accent: 'text-amber-600',
      hover: 'group-hover:bg-amber-600',
      icon: 'bg-amber-100 text-amber-600',
      border: 'border-amber-200',
      gradient: 'from-amber-50 to-white',
    },
    purple: {
      light: 'bg-purple-50',
      medium: 'bg-purple-100',
      accent: 'text-purple-600',
      hover: 'group-hover:bg-purple-600',
      icon: 'bg-purple-100 text-purple-600',
      border: 'border-purple-200',
      gradient: 'from-purple-50 to-white',
    },
    indigo: {
      light: 'bg-indigo-50',
      medium: 'bg-indigo-100',
      accent: 'text-indigo-600',
      hover: 'group-hover:bg-indigo-600',
      icon: 'bg-indigo-100 text-indigo-600',
      border: 'border-indigo-200',
      gradient: 'from-indigo-50 to-white',
    },
  };

  // Karty kontaktowe z danymi z konfiguracji
  const contactCards: ContactCard[] = [
    {
      id: 'address',
      icon: <LocationOn fontSize="large" />,
      title: 'Adres',
      content: (
        <>
          <p className="font-medium">{config.company.name}</p>
          <p>{config.contact.address.postal}</p>
          <p>{config.contact.address.city}</p>
          <p className="text-gray-500 text-sm mt-1">
            {config.contact.address.country}, {config.contact.address.region}
          </p>
        </>
      ),
      action: {
        text: 'Zobacz na mapie',
        url:
          config.social.google || 'https://maps.app.goo.gl/PHmh3NSaup2QxEeW6',
        icon: <OpenInNew fontSize="small" />,
      },
      color: 'blue',
    },
    {
      id: 'phone',
      icon: <Phone fontSize="large" />,
      title: 'Kontakt telefoniczny',
      content: (
        <>
          <a
            href={`tel:${config.contact.phone}`}
            className="text-lg font-medium hover:underline block"
          >
            {config.contact.phone}
          </a>
          <span className="text-sm text-gray-500">Dział handlowy</span>
          <a
            href="tel:+48609773932"
            className="text-lg font-medium hover:underline block mt-2"
          >
            +48 609 773 932
          </a>
          <span className="text-sm text-gray-500">Wsparcie techniczne</span>
        </>
      ),
      action: {
        text: 'Zadzwoń teraz',
        url: `tel:${config.contact.phone}`,
        icon: <Call fontSize="small" />,
      },
      color: 'green',
    },
    {
      id: 'email',
      icon: <Email fontSize="large" />,
      title: 'E-mail',
      content: (
        <>
          <a
            href={`mailto:${config.contact.email}`}
            className="text-lg font-medium hover:underline block"
          >
            {config.contact.email}
          </a>
          <span className="text-sm text-gray-500">Zapytania ogólne</span>
        </>
      ),
      action: {
        text: 'Napisz do nas',
        url: `mailto:${config.contact.email}`,
        icon: <Send fontSize="small" />,
      },
      color: 'red',
    },
    {
      id: 'hours',
      icon: <AccessTime fontSize="large" />,
      title: 'Godziny otwarcia',
      content: (
        <div className="space-y-1.5">
          <div className="flex justify-between">
            <span>Poniedziałek</span>
            <span className="font-medium">07:30 - 15:00</span>
          </div>
          <div className="flex justify-between">
            <span>Wtorek</span>
            <span className="font-medium">07:30 - 15:00</span>
          </div>
          <div className="flex justify-between">
            <span>Środa</span>
            <span className="font-medium">07:30 - 15:00</span>
          </div>
          <div className="flex justify-between">
            <span>Czwartek</span>
            <span className="font-medium">07:30 - 15:00</span>
          </div>
          <div className="flex justify-between">
            <span>Piątek</span>
            <span className="font-medium">07:30 - 15:00</span>
          </div>
          <div className="flex justify-between text-gray-500">
            <span>Sobota</span>
            <span>Zamknięte</span>
          </div>
          <div className="flex justify-between text-gray-500">
            <span>Niedziela</span>
            <span>Zamknięte</span>
          </div>
        </div>
      ),
      color: 'amber',
    },
    {
      id: 'store',
      icon: <Storefront fontSize="large" />,
      title: 'Sklep stacjonarny',
      content: (
        <>
          <p>
            Zapraszamy do odwiedzenia naszego sklepu stacjonarnego, gdzie nasi
            doradcy pomogą dobrać odpowiedni sprzęt do Twoich potrzeb.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Dostępna pełna ekspozycja osprzętu elektro-energetycznego.
          </p>
        </>
      ),
      color: 'purple',
    },
    {
      id: 'company',
      icon: <Business fontSize="large" />,
      title: 'O firmie',
      content: (
        <>
          <p>{config.company.slogan}</p>
        </>
      ),
      color: 'indigo',
    },
  ];

  // Social media z konfiguracji
  type SocialMedia = {
    icon: JSX.Element;
    label: string;
    href: string;
    color: string;
  };

  // Konfiguracja wszystkich możliwych mediów społecznościowych
  const socialMediaConfig: SocialMedia[] = [
    {
      icon: <Facebook />,
      label: 'Facebook',
      href: config.social.facebook || '',
      color: 'bg-blue-500 hover:bg-blue-600',
    },
    {
      icon: <YouTube />,
      label: 'YouTube',
      href: config.social.youtube || '',
      color: 'bg-red-600 hover:bg-red-700',
    },
    {
      icon: <Instagram />,
      label: 'Instagram',
      href: config.social.instagram || '',
      color: 'bg-pink-600 hover:bg-pink-700',
    },
    {
      icon: <Twitter />,
      label: 'Twitter',
      href: config.social.twitter || '',
      color: 'bg-blue-400 hover:bg-blue-500',
    },
    {
      icon: <LinkedIn />,
      label: 'LinkedIn',
      href: config.social.linkedin || '',
      color: 'bg-blue-700 hover:bg-blue-800',
    },
    {
      icon: <Stars />,
      label: 'Google',
      href: config.social.google || '',
      color: 'bg-amber-500 hover:bg-amber-600',
    },
  ];

  // Filtrowanie tylko tych mediów społecznościowych, które mają zdefiniowane URL-e
  const availableSocialMedia = socialMediaConfig.filter((item) => item.href);

  // Sprawdzenie czy są jakiekolwiek media społecznościowe do wyświetlenia
  const hasSocialMedia = availableSocialMedia.length > 0;

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, type: 'spring', stiffness: 100 },
    },
  };

  const mapVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, delay: 0.3 },
    },
  };

  return (
    <>
      <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen">
        {/* Hero section */}
        <motion.div
          className={`bg-gradient-to-r from-${config.theme.colors.primary}/10 to-${config.theme.colors.primary}/5 py-16 px-4 sm:px-6 lg:px-8`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="max-w-7xl mx-auto">
            <motion.h1
              className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span
                className={`bg-clip-text text-transparent bg-gradient-to-r from-${config.theme.colors.primary} to-blue-700`}
              >
                Kontakt z {config.company.name}
              </span>
            </motion.h1>
            <motion.p
              className="text-xl text-gray-600 max-w-3xl"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {config.company.slogan}. Masz pytania dotyczące naszych produktów
              lub usług? Jesteśmy tutaj, aby pomóc. Skontaktuj się z nami w
              najbardziej dogodny dla Ciebie sposób.
            </motion.p>
          </div>
        </motion.div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Grid kart kontaktowych */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {contactCards.map((card) => (
              <motion.div
                key={card.id}
                variants={itemVariants}
                className={`group relative rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border ${colorVariants[card.color].border}`}
                onMouseEnter={() => setHoveredCard(card.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Background gradient */}
                <div
                  className={`absolute inset-0 bg-gradient-to-b ${colorVariants[card.color].gradient} z-0`}
                ></div>

                {/* Animated background circle */}
                <motion.div
                  className={`absolute -right-8 -bottom-8 w-32 h-32 rounded-full ${colorVariants[card.color].medium} opacity-50 z-0`}
                  animate={{
                    scale: hoveredCard === card.id ? 1.2 : 1,
                    opacity: hoveredCard === card.id ? 0.7 : 0.5,
                  }}
                  transition={{ duration: 0.5 }}
                />

                <div className="relative z-10 p-6">
                  {/* Icon with animation */}
                  <motion.div
                    className={`w-14 h-14 rounded-full ${colorVariants[card.color].icon} flex items-center justify-center mb-4`}
                    whileHover={{ rotate: 10, scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 10 }}
                  >
                    {card.icon}
                  </motion.div>

                  <h3 className="text-xl font-bold mb-3">{card.title}</h3>

                  <div className="text-gray-700 space-y-1.5">
                    {card.content}
                  </div>

                  {card.action && (
                    <a
                      href={card.action.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1 mt-4 font-medium ${colorVariants[card.color].accent} hover:underline`}
                    >
                      {card.action.text}
                      {card.action.icon || '→'}
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Google Map section */}
          {config.contact.googleMaps && (
            <motion.div
              className="mb-12 rounded-xl overflow-hidden shadow-lg border border-gray-200"
              variants={mapVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="bg-gray-50 py-4 px-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <LocationOn className="mr-2 text-primary" /> Znajdź nas na
                  mapie
                </h2>
              </div>
              <div className="w-full h-[500px]">
                <iframe
                  title={`Google Map - ` + config.company.name}
                  src={config.contact.googleMaps}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </motion.div>
          )}

          {/* Social media section - wyświetlana tylko jeśli są media społecznościowe */}
          {hasSocialMedia && (
            <motion.div
              className="rounded-xl overflow-hidden shadow-md border border-gray-200 bg-white"
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <div className="bg-gray-50 py-4 px-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  Śledź nas w mediach społecznościowych
                </h2>
              </div>

              <div className="p-6">
                <p className="text-gray-600 mb-6">
                  Bądź na bieżąco z naszymi najnowszymi produktami, promocjami i
                  poradami. Odwiedź nasze profile:
                </p>

                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  {availableSocialMedia.map((social) => (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 px-4 py-3 rounded-lg text-white transition-all ${social.color}`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {social.icon}
                      <span>{social.label}</span>
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      <Seo title={`Kontakt`}></Seo>
    </>
  );
}

export default Contact;
